import React, { useState } from "react";
import {
  Button,
  Container,
  CircularProgress,
  Typography,
  Backdrop,
  Box,
  Alert,
  Snackbar,
  styled,
  LinearProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import * as XLSX from "xlsx";
import { DataGrid } from "@mui/x-data-grid";
import { UploadExcelDocumentsApi } from "../../Axios/Approval/api";

const MAX_FILE_SIZE_MB = 50;

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

const Equifax = () => {
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState("");
  const [resMessage, setResMessage] = useState("");
  const [errorExcel, setErrorExcel] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // ✅ NEW STATES
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadedFileName, setDownloadedFileName] = useState("");

  // ✅ ERROR STATES
  const [backendError, setBackendError] = useState("");
  const [showErrorBox, setShowErrorBox] = useState(false);

  const validateExcelFile = (file) => {
    const isValidType = /\.(xls|xlsx)$/i.test(file.name);
    const isValidSize = file.size / 1024 / 1024 < MAX_FILE_SIZE_MB;

    if (!isValidType || !isValidSize) {
      setResMessage(
        !isValidType
          ? "Please upload a valid Excel file (.xls or .xlsx)."
          : `File size should be less than ${MAX_FILE_SIZE_MB}MB.`
      );
      setErrorExcel(true);
      setSnackbarOpen(true);
      return false;
    }
    return true;
  };

  // ✅ UPDATED API HANDLER
  const uploadExcel = async (file) => {
    try {
      setUploadProgress(0);
      setDownloadProgress(0);
      setShowErrorBox(false);

      const response = await UploadExcelDocumentsApi(
        file,
        (progressEvent) => {
          if (progressEvent.total) {
            setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        },
        (progressEvent) => {
          if (progressEvent.total) {
            setDownloadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          }
        }
      );

      // ✅ DOWNLOAD FILE
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;

      let fileName = "bureau_reports.zip";
      const disposition = response.headers["content-disposition"];

      if (disposition) {
        const match = disposition.match(/filename="?(.+)"?/);
        if (match?.[1]) fileName = match[1];
      }

      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      setDownloadedFileName(fileName);

      setResMessage(`Downloaded: ${fileName}`);
      setErrorExcel(false);
      setSnackbarOpen(true);
    } catch (error) {
      let errorMessage = "Something went wrong.";

      try {
        if (error.response?.data) {
          const text = await error.response.data.text();
          const json = JSON.parse(text);
          errorMessage = json.error || json.message || text;
        }
      } catch {
        errorMessage = "Failed to read server error.";
      }

      setBackendError(errorMessage);
      setShowErrorBox(true);
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file || !validateExcelFile(file)) return;

    setLoading(true);
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: "binary" });
        const worksheet = workbook.Sheets[workbook.SheetNames[0]];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        const formatted = jsonData.map((row, i) => ({ ...row, id: i + 1 }));

        setColumns(
          Object.keys(formatted[0] || {}).map((key) => ({
            field: key,
            headerName: key,
            width: 150,
          }))
        );

        setRows(formatted);

        await uploadExcel(file);
      } catch {
        setBackendError("Invalid Excel file.");
        setShowErrorBox(true);
      } finally {
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  return (
    <>
      <Backdrop open={loading}>
        <CircularProgress />
      </Backdrop>

      <Snackbar open={snackbarOpen && !showErrorBox} autoHideDuration={3000}>
        <Alert severity={errorExcel ? "error" : "success"}>{resMessage}</Alert>
      </Snackbar>

      <Container>
        <Box sx={{ p: 2 }}>
          <label>
            <VisuallyHiddenInput type="file" onChange={handleFileUpload} />
            <Button variant="outlined" component="span" startIcon={<UploadFileIcon />}>
              Upload Excel
            </Button>
          </label>

          {fileName && <Typography mt={1}>File: {fileName}</Typography>}

          {/* ✅ PROGRESS */}
          {uploadProgress > 0 && uploadProgress < 100 && (
            <Box mt={2}>
              <Typography>Uploading: {uploadProgress}%</Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {downloadProgress > 0 && downloadProgress < 100 && (
            <Box mt={2}>
              <Typography>Downloading: {downloadProgress}%</Typography>
              <LinearProgress variant="determinate" value={downloadProgress} />
            </Box>
          )}

          {/* ✅ ERROR BOX */}
          {showErrorBox && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                border: "1px solid red",
                borderRadius: 2,
                backgroundColor: "#fdecea",
                position: "relative",
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              <Button
                onClick={() => setShowErrorBox(false)}
                sx={{ position: "absolute", right: 8, top: 8 }}
              >
                ✕
              </Button>

              <Typography variant="h6" color="error">
                Error
              </Typography>

              <Typography>{backendError}</Typography>
            </Box>
          )}

          {/* ✅ SUCCESS */}
          {downloadedFileName && (
            <Alert sx={{ mt: 2 }} severity="success">
              Downloaded: {downloadedFileName}
            </Alert>
          )}

          <Box sx={{ height: 400, mt: 2 }}>
            <DataGrid rows={rows} columns={columns} />
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default Equifax;